import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { Clock, LogOut } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { AddCardDialog } from '@/components/billing/add-card-dialog'
import { quoteTotal } from '@/lib/billing-format'
import { authApi } from '@/services/auth'
import { billingApi } from '@/services/billing'

export default function TrialExpiredPage() {
  const navigate = useNavigate()
  const [cardOpen, setCardOpen] = useState(false)

  const billingQuery = useQuery({
    queryKey: ['billing-status'],
    queryFn: billingApi.getStatus,
    staleTime: 0,
    retry: false,
  })
  const status = billingQuery.data

  // Nothing to block any more (a card was added).
  useEffect(() => {
    if (status && !status.is_locked) {
      navigate('/dashboard', { replace: true })
    }
  }, [status, navigate])

  const title =
    status?.lock_reason === 'subscription_ended' ? 'Your subscription has ended' : 'Your free trial has ended'
  const description =
    status?.lock_reason === 'subscription_ended'
      ? 'Your subscription was cancelled or its payment could not be collected. Subscribe again to keep using your account — your data and setup are safe.'
      : 'Add a payment card to keep using your account with no interruption to your data or setup.'

  const onSignOut = async () => {
    await authApi.logout()
    navigate('/login', { replace: true })
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-primary/5 px-4 py-10">
      <Card className="w-full max-w-md border-primary/20 shadow-xl">
        <CardHeader className="items-center text-center">
          <div className="mb-2 flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary">
            <Clock className="h-7 w-7" />
          </div>
          <CardTitle className="text-xl">{title}</CardTitle>
          <CardDescription>{description}</CardDescription>
          {status?.price?.month ? (
            <p className="pt-2 text-sm text-muted-foreground">
              {status.admin_count ?? 0} Admin / Manager and {status.worker_count ?? 0} Field Worker licences:{' '}
              {quoteTotal(status.price.month)}, or {quoteTotal(status.price.year ?? status.price.month)} paid annually.
              Customers and suppliers don&apos;t need a licence.
            </p>
          ) : null}
        </CardHeader>
        <CardContent className="space-y-3">
          <Button type="button" className="w-full" onClick={() => setCardOpen(true)}>
            {status?.lock_reason === 'subscription_ended' ? 'Update payment card' : 'Add payment card'}
          </Button>
          <Button type="button" variant="outline" className="w-full" onClick={onSignOut}>
            <LogOut className="mr-2 h-4 w-4" />
            Sign out
          </Button>
        </CardContent>
      </Card>
      <AddCardDialog
        open={cardOpen}
        onOpenChange={setCardOpen}
        onAdded={(next) => { if (!next.is_locked) navigate('/dashboard', { replace: true }) }}
      />
    </div>
  )
}
